/**
 * Pure helpers for the public-site WhatsApp click-to-chat entry point.
 *
 * This is a plain `wa.me` link — no Meta API call, no template approval. The
 * whole point is that the VISITOR sends the first message, which opens
 * Meta's 24h free-form customer-service window (see
 * docs/superpowers/specs/2026-08-04-whatsapp-ai-agent-inbox-design.md §3).
 */

export interface BuildWhatsappLinkParams {
  /** Destination phone number, any human formatting (spaces, +, dashes, parens). */
  number: string;
  /** Optional prefilled message. Omitted/empty => no `?text=` query at all. */
  message?: string;
}

/**
 * Builds a `https://wa.me/<digits>` link, optionally with a URL-encoded
 * prefilled message. Strips every non-digit character from `number` (the
 * leading `+`, spaces, dashes, parens, dots) since wa.me only accepts the
 * bare digit string (country code + number).
 */
export function buildWhatsappLink({ number, message }: BuildWhatsappLinkParams): string {
  const digits = number.replace(/\D/g, '');
  const base = `https://wa.me/${digits}`;
  if (!message) return base;
  return `${base}?text=${encodeURIComponent(message)}`;
}

export type WhatsappPrefillLocale = 'es' | 'en';

export interface WorkspaceWhatsappContext {
  /** Already-localized workspace title, as displayed on the page. */
  title: string;
  /** Canonical/current URL of the workspace page. */
  url: string;
}

const GENERIC_MESSAGE: Record<WhatsappPrefillLocale, string> = {
  es: 'Hola, busco un espacio de trabajo',
  en: "Hi, I'm looking for a workspace",
};

function workspaceMessage(
  locale: WhatsappPrefillLocale,
  title: string,
  url: string
): string {
  if (locale === 'en') {
    return `Hi, I'm interested in the space "${title}" (${url})`;
  }
  return `Hola, me interesa el espacio "${title}" (${url})`;
}

/**
 * Builds the localized, context-aware prefill copy for the WhatsApp button.
 * Falls back to the generic message unless BOTH a title and a url are given.
 */
export function buildWhatsappPrefill(
  locale: WhatsappPrefillLocale,
  workspace?: WorkspaceWhatsappContext
): string {
  if (workspace?.title && workspace?.url) {
    return workspaceMessage(locale, workspace.title, workspace.url);
  }
  return GENERIC_MESSAGE[locale];
}

/**
 * Decides whether the WhatsApp entry button should render at all. The
 * number comes from the public env var `WHATSAPP_PUBLIC_NUMBER`; if it's
 * missing/empty/whitespace-only or has no digits, we must NOT render a
 * broken `https://wa.me/` link.
 */
export function isWhatsappNumberConfigured(number: string | undefined | null): boolean {
  if (!number) return false;
  return number.replace(/\D/g, '').length > 0;
}
